/**
 * Race Registration Utility Functions
 *
 * Pure functions for race registration tracking:
 * - Event filtering and sorting
 * - Registration status resolution
 * - Report data transformation, filtering, sorting
 * - CSV generation and filename formatting
 *
 * Requirements: 2.1, 2.4, 2.5, 2.6, 5.2, 5.3, 6.2, 6.6, 7.2, 7.4, 7.5, 8.2, 8.3, 8.5
 */

import type { Event, Rider } from '@/api/rest-client';

/**
 * A transformed rider row for the Race Registration Report table.
 */
export interface ReportRow {
  rider_id: string;
  name: string;
  category: string;
  group: string;
  grade: string;
  race_registrations: Record<string, boolean>;
}

/**
 * Filter events to only those with event_type === 'Race'.
 *
 * Validates: Requirements 2.1, 5.2
 */
export function filterRaceEvents(events: Event[]): Event[] {
  return events.filter((e) => e.event_type === 'Race');
}

/**
 * Sort events by event_starttime ascending (chronological order).
 * Returns a new sorted array without mutating the input.
 *
 * Validates: Requirements 2.4, 6.7
 */
export function sortRaceEventsByStartTime(events: Event[]): Event[] {
  return [...events].sort((a, b) =>
    a.event_starttime.localeCompare(b.event_starttime),
  );
}

/**
 * Return true if the rider is registered for the given event, false otherwise.
 * Missing event IDs in the map are treated as not registered.
 *
 * Validates: Requirements 2.5, 6.4, 6.5
 */
export function getRegistrationStatus(
  registrations: Record<string, boolean>,
  eventId: string,
): boolean {
  return registrations[eventId] === true;
}

/**
 * Build a complete registration map from race events and a set of checked event IDs.
 * Every race event gets an entry: true if its event_id is in checkedEventIds, false otherwise.
 *
 * Validates: Requirements 2.6
 */
export function buildRegistrationMap(
  raceEvents: Event[],
  checkedEventIds: Set<string>,
): Record<string, boolean> {
  const map: Record<string, boolean> = {};
  for (const event of raceEvents) {
    map[event.event_id] = checkedEventIds.has(event.event_id);
  }
  return map;
}

/**
 * Filter riders to only active Student/Rider types.
 * Active means disabled is not '1' and not 1.
 *
 * Validates: Requirements 5.3
 */
export function filterActiveStudentRiders(riders: Rider[]): Rider[] {
  return riders.filter((r) => {
    const isStudentOrRider = r.type === 'Student' || r.type === 'Rider';
    const isActive = r.disabled !== '1' && r.disabled !== 1;
    return isStudentOrRider && isActive;
  });
}

/**
 * Transform a Rider into a ReportRow for the report table.
 * Missing category, group, and grade default to empty string.
 *
 * Validates: Requirements 6.2
 */
export function transformRiderReportRow(rider: Rider): ReportRow {
  return {
    rider_id: rider.rider_id,
    name: rider.name,
    category: rider.category ?? '',
    group: rider.group ?? '',
    grade: rider.grade ?? '',
    race_registrations: rider.race_registrations ?? {},
  };
}

/**
 * Sort report rows alphabetically by name ascending.
 * Returns a new sorted array without mutating the input.
 *
 * Validates: Requirements 6.6
 */
export function sortRidersByName(riders: ReportRow[]): ReportRow[] {
  return [...riders].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Apply group, category, grade filters AND search term to report rows.
 * An empty value for any filter matches all rows for that field.
 * Search is case-insensitive on the name field; category/group/grade are exact matches.
 *
 * categoryFilter and gradeFilter are optional to preserve backward compatibility.
 *
 * Validates: Requirements 7.2, 7.4, 7.5
 */
export function applyReportFilters(
  riders: ReportRow[],
  groupFilter: string,
  searchTerm: string,
  categoryFilter = '',
  gradeFilter = '',
): ReportRow[] {
  return riders.filter((r) => {
    const matchesGroup = !groupFilter || r.group === groupFilter;
    const matchesCategory = !categoryFilter || r.category === categoryFilter;
    const matchesGrade = !gradeFilter || r.grade === gradeFilter;
    const matchesSearch =
      !searchTerm || r.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesGroup && matchesCategory && matchesGrade && matchesSearch;
  });
}

/**
 * Generate a CSV string with headers and Yes/No registration values.
 * Headers: Name, Category, Group, Grade, then one column per race event (event name).
 * Race events should already be sorted by start time before calling this function.
 *
 * Validates: Requirements 8.2, 8.3
 */
export function generateRaceRegistrationCSV(
  riders: ReportRow[],
  raceEvents: Event[],
): string {
  const eventHeaders = raceEvents.map((e) => escapeCSVField(e.event_name));
  const headerLine = ['Name', 'Category', 'Group', 'Grade', '# of Races', ...eventHeaders].join(
    ',',
  );

  const dataLines = riders.map((rider) => {
    const baseFields = [
      escapeCSVField(rider.name),
      escapeCSVField(rider.category),
      escapeCSVField(rider.group),
      escapeCSVField(rider.grade),
      String(countRacesForRider(rider, raceEvents)),
    ];
    const registrationFields = raceEvents.map((event) =>
      getRegistrationStatus(rider.race_registrations, event.event_id) ? 'Yes' : 'No',
    );
    return [...baseFields, ...registrationFields].join(',');
  });

  // TOTAL row: per-race registered counts, plus total registrations under "# of Races".
  const perRaceCounts = countRegistrationsPerRace(riders, raceEvents);
  const totalRow = [
    'TOTAL',
    '',
    '',
    '',
    String(countTotalRegistrations(riders, raceEvents)),
    ...raceEvents.map((event) => String(perRaceCounts[event.event_id])),
  ].join(',');

  return [headerLine, ...dataLines, totalRow].join('\n');
}

/**
 * Count, for each race event, how many of the given riders are registered for it.
 * Returns a map of event_id -> count. Only the supplied race events are considered,
 * so stray/legacy registration entries for non-race or other-season events never
 * contribute to any count.
 *
 * Callers pass the currently filtered/shown riders so counts reflect active filters.
 *
 * Validates: FR-1, FR-2, FR-7; BR-1, BR-2, BR-5, BR-8
 */
export function countRegistrationsPerRace(
  riders: ReportRow[],
  raceEvents: Event[],
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const event of raceEvents) {
    counts[event.event_id] = riders.reduce(
      (n, rider) =>
        n + (getRegistrationStatus(rider.race_registrations, event.event_id) ? 1 : 0),
      0,
    );
  }
  return counts;
}

/**
 * Count how many of the given race events a single rider is registered for.
 *
 * Validates: FR-5; BR-1, BR-2
 */
export function countRacesForRider(row: ReportRow, raceEvents: Event[]): number {
  return raceEvents.reduce(
    (n, event) =>
      n + (getRegistrationStatus(row.race_registrations, event.event_id) ? 1 : 0),
    0,
  );
}

/**
 * Count how many riders are registered for at least one of the given race events.
 *
 * Validates: FR-4; BR-6
 */
export function countRidersRegisteredForAnyRace(
  riders: ReportRow[],
  raceEvents: Event[],
): number {
  return riders.reduce(
    (n, rider) => n + (countRacesForRider(rider, raceEvents) > 0 ? 1 : 0),
    0,
  );
}

/**
 * Sum of race registrations across all given riders and race events.
 * Equals the sum of countRegistrationsPerRace values, and the sum over riders
 * of countRacesForRider.
 *
 * Validates: FR-4; BR-4
 */
export function countTotalRegistrations(
  riders: ReportRow[],
  raceEvents: Event[],
): number {
  return riders.reduce((n, rider) => n + countRacesForRider(rider, raceEvents), 0);
}

/**
 * Escape a CSV field value. Wraps in quotes if it contains commas, quotes, or newlines.
 */
function escapeCSVField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Generate a filename like race-registration-report-YYYY-MM-DD.csv for the given date.
 *
 * Validates: Requirements 8.5
 */
export function generateRaceRegistrationCSVFilename(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `race-registration-report-${yyyy}-${mm}-${dd}.csv`;
}
